import { type Features } from '@/utilities/features';

const brand = `
    name
`;

const availability = `
    available
    availableFuture
    stock
`;

const breadcrumb = `
    name
    relativeUrl
    url
`;

const refinement = `
    id
    name
    query
    rangeMax
    rangeMin
    relativeUrl
    selected
    refinementCount
`;

const filterLayoutProperties = `
    contextualRefinements {
        ${refinement}
    }
    expanded
    multiColumn
    searchable
    selectedRangeMax
    selectedRangeMin
    validRangeMin
    validRangeMax
    slider
    swatches
    seoIndexable
`;

const filter = `
    id
    internalName
    layoutProperties {
        ${filterLayoutProperties}
    }
    name
    refinements {
        ${refinement}
    }
    popularRefinements {
        ${refinement}
    }
    selected
    visible
    removeQuery
`;

const image = `
    position
    type
    url
`;

const alternativeLanguages = `
    locale
    url
`;

const metaInformation = `
    query
    title
    description
    canonicalUrl
    alternativeLanguages {
        ${alternativeLanguages}
    }
`;

const page = `
    pageNumber
    query
    relativeUrl
`;

const pagination = `
    currentPage {
        ${page}
    }
    nextPage {
        ${page}
    }
    previousPage {
        ${page}
    }
    totalItemCount
    totalPageCount
    viewSize
`;

const price = `
    currencyCode
    type
    value
`;

const productDisplayProperties = `
    currentVariantSelected
    detailPageVariation
`;

const signing = `
    key
    text
`;

const signings = `
    discount {
        ${signing}
    }
    merchandise {
        ${signing}
    }
`;

const banner = `
  id
  position
  bannerContent {
    title
    signing
    description
    text
    image {
      alt
      src
      focusPoint {
        x
        y
      }
    }
    logo {
      alt
      src
    },
    link {
      title
      href
    }
  }
`;

const link = `
  title
  href
`;

const categoryPathNode = `
    id
    name
`;

const variantProduct = `
    availability {
        ${availability}
    }
    code
    lowestPriceFlag
    lowestPrice {
        value
    }
    color
    current
    selectionImage {
        ${image}
    }
    images {
        ${image}
    }
    overriddenPrices {
        ${price}
    }
    sellingPrice {
        ${price}
    }
    signings {
        ${signings}
    }
    trackingMetadata
    size
    url
`;

const product = `
    brand {
        ${brand}
    }
    categoryPath {
        ${categoryPathNode}
    }
    code
    availableFromPrice {
        ${price}
    }
    colorCount
    currentVariantProduct {
        ${variantProduct}
    }
    defaultVariantCode
    description
    designer
    displayName
    displayProperties {
        ${productDisplayProperties}
    }
    gift
    name
    subBrand {
        ${brand}
    }
    trackingMetadata
    url
    variantProducts (limit:4, groupBy: COLOR) {
        ${variantProduct}
    }
`;

const promotion = `
    componentId
    ownerId
    zone
    properties
`;

const searchAlternative = `
    corrected
    original
    products {
        ${product}
    }
    query
    totalItemCount
`;

const searchText = `
    alternatives {
        ${searchAlternative}
    }
    corrected
    original
`;

const sortOptions = `
    id
    name
    query
    relativeUrl
    selected
`;

const navigation = `
    breadcrumbs {
        ${breadcrumb}
    }
    filters {
        ${filter}
    }
    pagination {
        ${pagination}
    }
    products {
        ${product}
    }
    promotions {
        ${promotion}
    }
    searchText {
        ${searchText}
    }
    sortOptions {
        ${sortOptions}
    }
    relatedQueries
    trackingMetadata
    redirectUrl
`;

export const productListQuery = `
  banners {
    ${banner}
  }
  footerText
  footerTitle
  footerLinks {
    ${link}
  }
  headerText
  headerTitle
  metaInformation {
    ${metaInformation}
  }
  navigation {
    ${navigation}
  }
  ribbon {
    cta {
      href
      title
    }
    text
    variant
 }
  trackingMetadata
`;

export const productKeys = {
  base: ['products'] as const,
  list: (url: string, environment: string, locale: string) =>
    [...productKeys.base, 'list', url, environment, locale] as const,
};

type GetProductListQueryProps = {
  locale: string;
  url: string;
  userId?: string;
  variationType?: string;
  features?: Features;
};

type GetProductListSearchQuery = {
  keywords: string;
  locale: string;
  userId?: string;
};

// requires triple quotes to avoid encoding issues in graphql api
export const getProductListQuery = ({
  locale,
  url,
  userId,
  variationType,
  features,
}: GetProductListQueryProps) =>
  `query GetProductListing {
    productListing(
      query: """${decodeURIComponent(url)}""",
      locale: "${locale}",
      ${userId ? `userId: "${userId}"` : ''},
      ${variationType ? `variationType: "${variationType}"` : ''},
      ${features ? `features: ${features.toGraphQLInputLiteral()}` : ''},
    ) {
      ${productListQuery}
    }
}`;

export const getProductListSearchQuery = ({
  locale,
  keywords,
  userId,
}: GetProductListSearchQuery) =>
  `query GetProductListing {
      productListingSearch(
        keywords: "${keywords}",
        locale: "${locale}",
        ${userId ? `userId: "${userId}"` : ''},
      ) {
        ${productListQuery}
      }
  }`;
